import { useEffect, useState } from "react";
import { Plus, Trash2, Moon, Save, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { apiGet, apiMutate } from "@/lib/api";
import { toast } from "sonner";

/**
 * PrepTomorrowCard — Option D: write your own 3 moves for tomorrow.
 *
 * Design intent:
 * - Collapsed by default (doesn't distract from today's 3 moves).
 * - Expands to reveal 1-3 slots. User can add/remove.
 * - Each slot: title, expected result, time chip (5/10/15), dimension, optional route.
 * - Save → overrides tomorrow's auto-generated set. Reset → back to auto.
 * - Copy stays motivational: "Plan tomorrow, sleep lighter."
 */

const DIMENSIONS = [
  { id: "consistency", label: "Consistency", icon: "🎬" },
  { id: "planning",    label: "Planning",    icon: "💡" },
  { id: "engagement",  label: "Engagement",  icon: "💬" },
  { id: "analytics",   label: "Analytics",   icon: "📊" },
  { id: "networking",  label: "Networking",  icon: "🤝" },
];

const ROUTES = [
  { id: "",           label: "None — I'll log it manually" },
  { id: "/scripts",   label: "Scripts (Script Writer)" },
  { id: "/pipeline",  label: "Pipeline" },
  { id: "/vault",     label: "Vault" },
  { id: "/calendar",  label: "Calendar" },
  { id: "/analytics", label: "Analytics" },
  { id: "/goals",     label: "Goals" },
  { id: "/settings",  label: "Settings" },
];

const TIME_OPTIONS = [5, 10, 15];

const emptyMove = () => ({
  title: "",
  expected_result: "",
  time_min: 10,
  dimension: "consistency",
  action_route: "",
});

export default function PrepTomorrowCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tomorrow, setTomorrow] = useState("");
  const [isPrepped, setIsPrepped] = useState(false);
  const [drafts, setDrafts] = useState([emptyMove()]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiGet("/today/prep-tomorrow");
        setTomorrow(r.tomorrow);
        setIsPrepped(!!r.is_user_prepped);
        if (r.is_user_prepped && r.moves?.length) {
          setDrafts(r.moves.map((m) => ({
            title: m.title,
            expected_result: m.expected_result,
            time_min: m.time_min,
            dimension: m.dimension,
            action_route: m.action?.route || "",
          })));
        }
      } catch (e) {
        // silent fail — feature is optional
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function updateDraft(i, patch) {
    setDrafts((cur) => cur.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }

  function addSlot() {
    if (drafts.length >= 3) return;
    setDrafts((cur) => [...cur, emptyMove()]);
  }

  function removeSlot(i) {
    if (drafts.length === 1) return;
    setDrafts((cur) => cur.filter((_, idx) => idx !== i));
  }

  async function save() {
    // Validate
    for (const d of drafts) {
      if (!d.title.trim() || !d.expected_result.trim()) {
        toast.error("Every move needs a title and expected result.");
        return;
      }
      if (d.title.length > 140 || d.expected_result.length > 140) {
        toast.error("Keep titles and results under 140 characters.");
        return;
      }
    }
    setSaving(true);
    try {
      const r = await apiMutate("POST", "/today/prep-tomorrow", { moves: drafts });
      setIsPrepped(true);
      setTomorrow(r.tomorrow);
      toast.success("Tomorrow's 3 moves are locked in 🌙", {
        description: "You just made tomorrow easier for future-you.",
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save — try again";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setSaving(true);
    try {
      await apiMutate("DELETE", "/today/prep-tomorrow", {});
      setIsPrepped(false);
      setDrafts([emptyMove()]);
      toast("Reset — tomorrow will use auto-generated moves.");
    } catch (e) {
      toast.error("Couldn't reset — try again");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  const dateLabel = tomorrow
    ? new Date(tomorrow).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })
    : "tomorrow";

  return (
    <div
      data-testid="prep-tomorrow-card"
      className="rounded-2xl overflow-hidden mt-10"
      style={{
        background: "var(--surface)",
        border: `1px solid ${isPrepped ? "rgba(216,168,87,0.45)" : "var(--ink-22)"}`,
      }}
    >
      <button
        data-testid="prep-tomorrow-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 sm:px-6 py-4 text-left"
        style={{ background: "transparent" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Moon size={16} strokeWidth={1.7} style={{ color: "var(--seal)" }} />
          <div className="min-w-0">
            <div className="font-heading text-base sm:text-lg" style={{ color: "var(--ink)" }}>
              Prep tomorrow&apos;s 3 moves
            </div>
            <div className="text-[11px] sm:text-xs mt-0.5" style={{ color: "var(--ink-55)" }}>
              {isPrepped
                ? <>You&apos;ve locked in {drafts.length} custom move{drafts.length > 1 ? "s" : ""} for {dateLabel}.</>
                : <>Plan tomorrow, sleep lighter. Auto-moves ready for {dateLabel} if you skip.</>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isPrepped && (
            <span
              data-testid="prep-tomorrow-badge"
              className="text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-full"
              style={{ background: "rgba(216,168,87,0.10)", border: "1px solid rgba(216,168,87,0.45)", color: "var(--seal)" }}
            >
              Locked
            </span>
          )}
          {open ? <ChevronUp size={16} style={{ color: "var(--ink-55)" }} /> : <ChevronDown size={16} style={{ color: "var(--ink-55)" }} />}
        </div>
      </button>

      {open && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex flex-col gap-4" style={{ borderTop: "1px solid var(--ink-22)" }}>
          <div className="text-xs pt-4" style={{ color: "var(--ink-72)" }}>
            Write up to <span style={{ color: "var(--seal)" }}>3 moves</span> you want to tackle tomorrow. They&apos;ll replace the auto-generated set for {dateLabel}. Leave the action route blank if it&apos;s a manual task — you&apos;ll log it inline.
          </div>

          {drafts.map((d, i) => (
            <div
              key={i}
              data-testid={`prep-slot-${i}`}
              className="rounded-xl p-4 flex flex-col gap-3"
              style={{ background: "var(--bg)", border: "1px solid var(--ink-22)" }}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--seal)" }}>
                  Tomorrow · Move {i + 1}
                </span>
                {drafts.length > 1 && (
                  <button
                    data-testid={`prep-remove-${i}`}
                    onClick={() => removeSlot(i)}
                    className="p-1 rounded transition-colors"
                    style={{ color: "var(--ink-55)" }}
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>

              <input
                data-testid={`prep-title-${i}`}
                placeholder="What are you doing? (e.g. Record Day 39 reel)"
                value={d.title}
                onChange={(e) => updateDraft(i, { title: e.target.value })}
                maxLength={140}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: "var(--surface)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
              />

              <input
                data-testid={`prep-expected-${i}`}
                placeholder="Expected result (e.g. 1 reel published)"
                value={d.expected_result}
                onChange={(e) => updateDraft(i, { expected_result: e.target.value })}
                maxLength={140}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: "var(--surface)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <div>
                  <label className="block text-[9px] uppercase tracking-[0.22em] mb-1" style={{ color: "var(--ink-55)" }}>Time</label>
                  <div className="flex gap-1">
                    {TIME_OPTIONS.map((t) => (
                      <button
                        key={t}
                        data-testid={`prep-time-${i}-${t}`}
                        onClick={() => updateDraft(i, { time_min: t })}
                        className="flex-1 py-1.5 text-xs rounded-md transition-colors"
                        style={{
                          background: d.time_min === t ? "rgba(216,168,87,0.12)" : "var(--surface)",
                          border: `1px solid ${d.time_min === t ? "var(--seal)" : "var(--ink-22)"}`,
                          color: d.time_min === t ? "var(--seal)" : "var(--ink-72)",
                        }}
                      >
                        {t}m
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-[0.22em] mb-1" style={{ color: "var(--ink-55)" }}>Focus area</label>
                  <select
                    data-testid={`prep-dim-${i}`}
                    value={d.dimension}
                    onChange={(e) => updateDraft(i, { dimension: e.target.value })}
                    className="w-full px-2.5 py-1.5 rounded-md text-xs"
                    style={{ background: "var(--surface)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
                  >
                    {DIMENSIONS.map((dim) => (
                      <option key={dim.id} value={dim.id}>{dim.icon} {dim.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-[0.22em] mb-1" style={{ color: "var(--ink-55)" }}>Opens</label>
                  <select
                    data-testid={`prep-route-${i}`}
                    value={d.action_route}
                    onChange={(e) => updateDraft(i, { action_route: e.target.value })}
                    className="w-full px-2.5 py-1.5 rounded-md text-xs"
                    style={{ background: "var(--surface)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
                  >
                    {ROUTES.map((r) => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}

          {drafts.length < 3 && (
            <button
              data-testid="prep-add-slot"
              onClick={addSlot}
              className="text-xs inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-lg"
              style={{ background: "transparent", border: "1px dashed var(--ink-22)", color: "var(--ink-72)" }}
            >
              <Plus size={12} /> Add another move ({drafts.length}/3)
            </button>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button
              data-testid="prep-save"
              onClick={save}
              disabled={saving}
              className="btn-primary justify-center flex-1"
            >
              <Save size={13} /> {isPrepped ? "Update tomorrow" : "Lock in tomorrow"}
            </button>
            {isPrepped && (
              <button
                data-testid="prep-reset"
                onClick={reset}
                disabled={saving}
                className="px-4 py-2.5 rounded-lg text-xs uppercase tracking-[0.16em] inline-flex items-center justify-center gap-1.5"
                style={{ background: "transparent", border: "1px solid var(--ink-22)", color: "var(--ink-72)" }}
              >
                <RotateCcw size={12} /> Reset to auto
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
